/**
 * Owns the receiver's low-latency hints for one guest session.
 *
 * `jitterBufferTarget` (ms) and Chromium's older `playoutDelayHint` (seconds) ask the engine
 * for a minimum playout target. Until now they were written once, from deprecated `libs/Peer`
 * code, and never observed again — so nobody could tell whether the hint was in force when a
 * ~320 ms buffer stood up on a 5 ms LAN. This controller applies the hints on the track event,
 * can re-assert or pulse them (the Phase 5 drain levers), and reads back what the receiver
 * actually reports so the figure rides `clientStats` every tick.
 *
 * Mirrors the deprecated `applyLowLatencyReceiverHints` (libs/Peer presets.ts): feature-detect
 * `playoutDelayHint` with `in`, seconds = ms / 1000, validate 0..RECEIVER_HINT_MAX_MS. The
 * receiver is typed structurally so tests use a plain object.
 */

import { SHARED_AUDIO_JITTER_BUFFER_TARGET_MS } from '../playoutDelay';
import {
  RECEIVER_HINT_LOW_LATENCY_TARGET_MS,
  RECEIVER_HINT_MAX_MS,
  type ReceiverHintReadback,
} from './receiverHintContracts';

export type HintableReceiver = { jitterBufferTarget?: number | null; playoutDelayHint?: number | null };

/** The receiver's track, as far as this controller needs to know it. */
type TrackedReceiver = HintableReceiver & { track?: { kind: string } | null };

/** Anything that enumerates receivers — an RTCPeerConnection, structurally. */
export type VideoReceiverSource = { getReceivers?: () => ReadonlyArray<TrackedReceiver> };

export type ReceiverLatencyHintControllerOptions = {
  /** performance.now()-domain clock for `appliedAtMs`. */
  nowMs?: () => number;
  /** Where invalid targets and engine rejections are reported. */
  log?: (line: string) => void;
  externallyOwned?: (kind: 'video' | 'audio', property: 'jitterBufferTarget' | 'playoutDelayHint') => boolean;
};

function defaultNowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export class ReceiverLatencyHintController {
  private readonly nowMs: () => number;
  private readonly log: (line: string) => void;
  private readonly externallyOwned?: ReceiverLatencyHintControllerOptions['externallyOwned'];
  private receiver: HintableReceiver | null = null;
  private lastTarget: number | null = null;
  private appliedAtMs: number | null = null;
  /** Game-audio receiver. Separate from `receiver` so a drain pulse cannot move video. */
  private audioReceiver: HintableReceiver | null = null;
  private audioAppliedAtMs: number | null = null;

  constructor(options: ReceiverLatencyHintControllerOptions = {}) {
    this.nowMs = options.nowMs ?? defaultNowMs;
    this.log = options.log ?? ((line) => console.warn(line));
    this.externallyOwned = options.externallyOwned;
  }

  /** Attach the video receiver; null detaches both video and audio while retaining the last video target. */
  attach(receiver: HintableReceiver | null): void {
    if (this.receiver === receiver) {
      if (receiver === null) this.clearAudio();
      return;
    }
    this.receiver = receiver;
    this.appliedAtMs = null;
    if (receiver === null) this.clearAudio();
  }

  /**
   * Attach the connection's video receiver and apply the low-latency target. Called on the
   * remote video track event: the engine re-derives its hints when media starts flowing, so a
   * value set at pre-negotiation does not necessarily survive to the first decoded frame.
   * Detaches when the connection exposes no video receiver.
   */
  attachVideoReceiverOf(source: VideoReceiverSource | null): ReceiverHintReadback | null {
    const receivers = typeof source?.getReceivers === 'function' ? source.getReceivers() : [];
    const video = receivers.find((receiver) => receiver.track?.kind === 'video') ?? null;
    this.attach(video);
    return video ? this.apply(RECEIVER_HINT_LOW_LATENCY_TARGET_MS) : null;
  }

  /**
   * Attach the first audio receiver and apply the shared NetEq floor
   * (`SHARED_AUDIO_JITTER_BUFFER_TARGET_MS`) without changing video. Returns the hint
   * readback, or null and detaches the previous audio receiver if none is found.
   * Setter failures are caught and still read back; the hints are a floor, not a cap.
   * Receiver-access errors propagate.
   */
  attachAudioReceiverOf(source: VideoReceiverSource | null): ReceiverHintReadback | null {
    const receivers = typeof source?.getReceivers === 'function' ? source.getReceivers() : [];
    const audio = receivers.find((receiver) => receiver.track?.kind === 'audio') ?? null;
    this.audioReceiver = audio;
    this.audioAppliedAtMs = null;
    if (!audio) return null;
    const readback = this.writeHints(audio, SHARED_AUDIO_JITTER_BUFFER_TARGET_MS);
    if (!readback) return null;
    this.audioAppliedAtMs = readback.appliedAtMs;
    return readback;
  }

  /**
   * Set both hints to `targetMs` and read them back. Null when detached or when the target is
   * outside 0..RECEIVER_HINT_MAX_MS (the receiver is left untouched in that case).
   * Setter failures are caught and still read back; the requested target is remembered
   * even if rejected. Receiver getter errors propagate.
   */
  apply(targetMs: number): ReceiverHintReadback | null {
    const receiver = this.receiver;
    if (!receiver) return null;
    const readback = this.writeHints(receiver, targetMs);
    if (!readback) return null;
    this.lastTarget = targetMs;
    this.appliedAtMs = readback.appliedAtMs;
    return readback;
  }

  /** Re-apply the last target (a drain lever: the engine re-evaluates the hint on write). */
  reassert(): ReceiverHintReadback | null {
    if (this.lastTarget === null) return null;
    return this.apply(this.lastTarget);
  }

  /**
   * Set `targetMs`, then restore the previous target on the next macrotask. Resolves with the
   * read-back after the restore. A pulse with no prior target restores the low-latency default.
   */
  pulse(targetMs: number): Promise<ReceiverHintReadback | null> {
    const restoreTo = this.lastTarget ?? RECEIVER_HINT_LOW_LATENCY_TARGET_MS;
    if (this.apply(targetMs) === null) return Promise.resolve(null);
    return new Promise((resolve) => {
      setTimeout(() => resolve(this.apply(restoreTo)), 0);
    });
  }

  /** What the video receiver reports right now; null when detached or never applied. */
  readback(): ReceiverHintReadback | null {
    return this.readReceiver(this.receiver, this.appliedAtMs);
  }

  /** What the audio receiver reports right now; null when detached or never applied. */
  audioReadback(): ReceiverHintReadback | null {
    return this.readReceiver(this.audioReceiver, this.audioAppliedAtMs);
  }

  private clearAudio(): void {
    this.audioReceiver = null;
    this.audioAppliedAtMs = null;
  }

  /** Writes both hints. Null when `targetMs` is out of range; a thrown setter is logged and still read back. */
  private writeHints(receiver: HintableReceiver, targetMs: number): ReceiverHintReadback | null {
    if (!Number.isFinite(targetMs) || targetMs < 0 || targetMs > RECEIVER_HINT_MAX_MS) {
      this.log(`[ReceiverHints] Invalid jitterBufferTarget ${targetMs}ms; must be 0-${RECEIVER_HINT_MAX_MS}ms`);
      return null;
    }
    try {
      const kind = receiver === this.audioReceiver ? 'audio' : 'video';
      if ('jitterBufferTarget' in receiver && !this.externallyOwned?.(kind, 'jitterBufferTarget')) receiver.jitterBufferTarget = targetMs;
      // playoutDelayHint is in SECONDS. Feature-detected: Chromium is removing it.
      if ('playoutDelayHint' in receiver && !this.externallyOwned?.(kind, 'playoutDelayHint')) receiver.playoutDelayHint = targetMs / 1000;
    } catch (error) {
      this.log(`[ReceiverHints] Failed to set receiver hints: ${String(error)}`);
    }
    return this.readReceiver(receiver, this.nowMs());
  }

  private readReceiver(receiver: HintableReceiver | null, appliedAtMs: number | null): ReceiverHintReadback | null {
    if (!receiver || appliedAtMs === null) return null;
    const hintSeconds = 'playoutDelayHint' in receiver ? finiteOrNull(receiver.playoutDelayHint) : null;
    return {
      jitterBufferTargetMs: finiteOrNull(receiver.jitterBufferTarget),
      playoutDelayHintMs: hintSeconds === null ? null : hintSeconds * 1000,
      appliedAtMs,
    };
  }

  /** The last target successfully applied; null before the first apply. */
  lastTargetMs(): number | null {
    return this.lastTarget;
  }
}
